import { useEffect, useMemo, useCallback, type JSX } from 'react';
import key from 'weak-key';

// utils
import { useCurrentLanguage } from './useCurrentLanguage';

function addScript(scriptUrl: string, toolId: string, identifier: string, currentLanguage: string) {
  const script = document.createElement('script');
  script.src = scriptUrl;
  script.id = `script-${toolId}`;
  script.type = 'text/javascript';
  script.onload = () => {
    if (window[identifier] && typeof window[identifier].init === 'function') {
      const langCode = currentLanguage.includes('-')
        ? currentLanguage.split('-')
        : [currentLanguage, currentLanguage];
      window[identifier].init({
        containerSelector: `#${toolId}`,
        localisationCode: `${langCode[0].toLowerCase()}-${langCode[1].toUpperCase()}`,
        mode: 'embedded',
      });
    }
  };
  document.head.appendChild(script);
}

function addStyle(styleUrl: string, toolId: string) {
  const style = document.createElement('link');
  style.href = styleUrl;
  style.id = `style-${toolId}`;
  style.type = 'text/css';
  style.rel = 'stylesheet';
  document.head.appendChild(style);
}

export function useCustomTool(
  addScripts: boolean,
  identifier: string | undefined,
  scriptUrl: string | undefined,
  styleUrl: string | undefined,
): () => JSX.Element {
  const toolId = useMemo(() => `tool-${key({ identifier })}`, [identifier]);
  const currentLanguage = useCurrentLanguage();

  useEffect(() => {
    if (scriptUrl && addScripts && identifier) {
      try {
        if (!document.getElementById(`script-${toolId}`)) {
          addScript(scriptUrl, toolId, identifier, currentLanguage);
        }

        if (styleUrl && !document.getElementById(`style-${toolId}`)) {
          addStyle(styleUrl, toolId);
        }
      } catch (e) {
        /* no logged errors for fastlane tools */
      }
    }
  }, [addScripts, scriptUrl, toolId, identifier, currentLanguage, styleUrl]);

  const render = useCallback(() => {
    // rerender the container if the scriptUrl changes
    return <div id={toolId} key={scriptUrl} />;
  }, [scriptUrl, toolId]);

  return render;
}
